import {
  Button,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router";
import { LabsCard } from "../components/LabsCard.tsx";
import { OnboardingStepper } from "../components/OnboardingStepper";
import { onboardingSteps } from "../data/onboardingSteps";
import { trackEvent } from "../util/analytics";
import { EventCategory } from "@labs/shared/types/EventCategory";
import { HeroHeader } from "../components/HeroHeader.tsx";
import solutionsBanner from "../assets/banners/solutions.png";
import { useLocalSolutionMapState } from "../util/useLocalSolutionMapState.ts";
import { useMemo } from "react";
import { Scope } from "@labs/shared/types/Solution.ts";
import scopeReferenceImage from "../assets/scopeReference.png";
import { SummaryAccordion } from "../components/SummaryAccordion.tsx";
import { generateSolutionCopyByScope } from "@labs/shared/util/generateSolutionCopyByScope.ts";
import { ModalType } from "../types/ModalType.ts";
import { useModal } from "../util/modal/useModal.ts";
import { CarbonImpactTable } from "../components/CarbonImpactTable.tsx";
import { analyzePlan } from "../util/planAnalysis/analyzePlan.ts";
import { useLocalTargetState } from "../util/useLocalTargetState.ts";
import { useLocalBaselineEmissionsState } from "../util/useLocalBaselineEmissionsState.ts";
import { ButtonMode } from "../types/ButtonMode.ts";
import { PlanSummaryAccordion } from "../components/PlanSummaryAccordion.tsx";
import { PlanComponentPageKey } from "@labs/shared/types/PlanComponentPageKey.ts";

const SolutionsLanding = () => {
  const theme = useTheme();
  const { openModal } = useModal();
  const [solutionMap] = useLocalSolutionMapState();
  const [target] = useLocalTargetState();
  const [baselineEmissions] = useLocalBaselineEmissionsState();
  const solutionCopyByScope = useMemo(
    () => generateSolutionCopyByScope(solutionMap),
    [solutionMap],
  );
  const analyzedPlan = useMemo(
    () => analyzePlan(solutionMap, target, baselineEmissions),
    [baselineEmissions, solutionMap, target],
  );

  return (
    <Grid container spacing={4}>
      <Grid size={12}>
        <Stack direction={"column"} spacing={4}>
          <OnboardingStepper activeStep={2} steps={onboardingSteps} />
          <HeroHeader
            title="Decarbonization Solutions"
            description="It’s time to build your emissions reduction plan! We will guide you
          through a comprehensive set of decarbonization solutions. As you apply
          solutions, you’ll see your plan take shape. Don’t worry – you’ll be
          able to iterate and tweak your plan later."
            showCalculateLink
            backgroundImage={solutionsBanner}
          />
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Stack direction={"column"} spacing={2}>
          <LabsCard>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"baseline"}
            >
              <Typography variant="h5" color="primary">
                Scope 1
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Direct Emissions
              </Typography>
            </Stack>

            <Typography variant="body1">
              Emissions from combustion in owned or controlled boilers,
              furnaces, vehicles, and process equipment.
            </Typography>
            <Divider />
            <PlanSummaryAccordion
              planComponentPageKey={PlanComponentPageKey.Scope1CutEnergyWaste}
              header={"Cut Energy Waste"}
              textContent={[
                {
                  title: "Energy Efficiency",
                  borderColor: theme.palette.primary.light,
                  listItems: solutionCopyByScope[Scope.Scope1].cutEnergyWaste,
                },
              ]}
            />
            <PlanSummaryAccordion
              planComponentPageKey={PlanComponentPageKey.Scope1Electrify}
              header={"Electrify"}
              textContent={[
                {
                  title: null,
                  borderColor: theme.palette.primary.light,
                  listItems: solutionCopyByScope[Scope.Scope1].electrify,
                },
              ]}
            />
            <PlanSummaryAccordion
              planComponentPageKey={PlanComponentPageKey.Scope1BuyClean}
              header={"Buy Clean"}
              textContent={[
                {
                  title: "Low or No-Carbon Fuel Switching",
                  borderColor: theme.palette.primary.light,
                  listItems: solutionCopyByScope[Scope.Scope1].buyClean,
                },
              ]}
            />
            <PlanSummaryAccordion
              planComponentPageKey={
                PlanComponentPageKey.Scope1LessCommonSolutions
              }
              header={"Less Common Solutions"}
              textContent={[
                {
                  title: null,
                  borderColor: theme.palette.primary.light,
                  listItems:
                    solutionCopyByScope[Scope.Scope1].lessCommonSolutions,
                },
              ]}
            />
          </LabsCard>
          <LabsCard>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"baseline"}
            >
              <Typography variant="h5" color="primary">
                Scope 2
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Indirect Emissions
              </Typography>
            </Stack>
            <Typography variant="body1">
              Emissions from the generation of purchased electricity, steam,
              heating, and cooling consumed by your organization.
            </Typography>
            <Divider />
            <PlanSummaryAccordion
              planComponentPageKey={PlanComponentPageKey.Scope2CutEnergyWaste}
              header={"Cut Energy Waste"}
              textContent={[
                {
                  title: "Energy Efficiency",
                  borderColor: theme.palette.primary.light,
                  listItems: solutionCopyByScope[Scope.Scope2].cutEnergyWaste,
                },
              ]}
            />
            <PlanSummaryAccordion
              planComponentPageKey={PlanComponentPageKey.Scope2BuyClean}
              header={"Buy Clean"}
              textContent={[
                {
                  title: null,
                  borderColor: theme.palette.primary.light,
                  listItems: solutionCopyByScope[Scope.Scope2].buyClean,
                },
              ]}
            />
          </LabsCard>
          <LabsCard>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"baseline"}
            >
              <Typography variant="h5" color="primary">
                Scope 3
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Indirect Emissions
              </Typography>
            </Stack>
            <SummaryAccordion
              header={"Supply Chain Emissions"}
              buttonHref={"/contact-us"}
              buttonText={"Call Us"}
              buttonMode={ButtonMode.Edit}
              textContent={[
                {
                  title: null,
                  borderColor: theme.palette.primary.light,
                  listItems: [
                    "Understand your product footprint(s) using actionable lifecycle assessments.",
                    "Align your supply chain with your decarbonization goals, ensuring that your vendors and partners also contribute to your sustainability objectives.",
                    "Strengthen supplier relationships by collaborating on sustainability initiatives, reducing emissions and improving the environmental impact across your value chain.",
                    "Demonstrate leadership by promoting sustainable practices throughout your supply chain, contributing to a broader industry shift toward decarbonization.",
                  ],
                },
              ]}
            />
            <img
              width={"100%"}
              src={scopeReferenceImage}
              alt="Diagram explaining the difference between Scope 1, 2 and 3 emissions"
            />
          </LabsCard>
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Stack direction={"column"} spacing={4}>
          <LabsCard sx={{ height: "auto" }}>
            <Typography variant="h5" color="primary">
              Carbon Impact
            </Typography>
            <Typography variant="body1">
              We represent your base year emissions as 100 carbon units and
              perform all calculations in relation to those 100 units.{" "}
              <Link
                onClick={() => {
                  openModal(ModalType.Calculation);
                  trackEvent(
                    EventCategory.Plan,
                    "Opened Calculate Modal",
                    window.location.href,
                  );
                }}
              >
                Learn more
              </Link>{" "}
              about how we calculate your plan.
            </Typography>
            <Divider />
            <CarbonImpactTable analyzedPlan={analyzedPlan} target={target} />
          </LabsCard>
          <Button
            variant={"outlined"}
            component={RouterLink}
            to={"/dashboard"}
            sx={{ alignSelf: "flex-end" }}
          >
            Your plan
          </Button>
        </Stack>
      </Grid>

      <Button variant={"outlined"} component={RouterLink} to="/baseline">
        Back
      </Button>
    </Grid>
  );
};

export default SolutionsLanding;
